/**
 * `/zadania` — the whole attention queue as one deck, worked one card at a time.
 *
 * Composition. `lib/useTasksScreen` owns the two reads and the deck, `lib/useHeldResponses` owns the
 * one write and its five-second hold, `model/` owns every rule, and this file decides what stands
 * where and in which of four states.
 *
 * Canon: `docs/design/zadania-2026-09-18/zadania-c-runda5.html`, direction C "Fokus", round 3 — the
 * index at 320, the card at 836 centred, the decision buttons alone under it, the legend on the
 * line of the muted sentence, and a subtle deck edge nobody may strengthen.
 *
 * ── Four states, and none of them is a branch on the data ────────────────────────────────────
 * `pending` while either read is open, `error` when the queue failed, `empty` when it answered with
 * nothing, `ready` otherwise. `x.length === 0` cannot tell the first from the third.
 *
 * ── The celebration belongs to an act done HERE ──────────────────────────────────────────────
 * A deck the trainer cleared draws `QueueDone` with its tallies. A queue that was already empty when
 * they arrived is TOLD — the same `EmptyState` sentence, no tallies, no congratulation — and nothing
 * about either is persisted anywhere.
 *
 * ── What the trainer did is announced in both channels ───────────────────────────────────────
 * One live region for the whole screen, mounted empty; every settled write speaks through
 * `useAttentionResponse`, and an undo speaks here, because nothing is sent for it to report.
 */

import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import {
  mutedSentence,
  MutedNote,
  snoozePresetAt,
  snoozePresets,
  snoozeTermWords,
  taskLabel,
  type AttentionResponseCommand,
  type SnoozePreset,
} from '~/features/attention-queue';
import {
  deckSwap,
  screenMotion,
  useMotionOnKeyChange,
  useScreenMotionPreference,
  type DeckSide,
} from '~/shared/design-system/motion';
import { buildClientRoster, rosterQueryOptions } from '~/entities/client';
import { ChatDock, useClientChat } from '~/features/chat';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { useInPlaceSession } from '~/shared/lib/useInPlaceSession';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import shell from '~/shared/ui/canonScreen.module.css';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';

import { TASK_PRIMARY_ATTRIBUTE } from '../lib/taskMarks';
import { useImplicitResolution } from '../lib/useImplicitResolution';
import { useHeldResponses } from '../lib/useHeldResponses';
import { useTaskShortcuts } from '../lib/useTaskShortcuts';
import { useTaskSlots } from '../lib/useTaskSlots';
import { useTasksScreen } from '../lib/useTasksScreen';
import type { HandledKind } from '../model/taskDeck';
import {
  bulkTargets,
  cardsInScope,
  deckCounts,
  deckIsCleared,
  deckProgress,
  deckSummary,
  deckTally,
  markHandled,
  resolveImplicitly,
  selectCard,
  selectedCard,
  selectedPosition,
  setScope,
  stepSelection,
  unmarkHandled,
  type BulkAct,
  type DeckCard,
  type DeckScope,
} from '../model/taskDeck';
import { BulkMenu } from './BulkMenu';
import { DecisionBar } from './DecisionBar';
import { QueueDone } from './QueueDone';
import { TaskCard } from './TaskCard';
import { CogIcon } from './TaskIcons';
import { TaskQueueIndex } from './TaskQueueIndex';
import styles from './tasks.module.css';

interface TasksScreenProps {
  /**
   * The one session panel, laid over this screen by a card's „Zobacz sesję” — composed by the route,
   * because the panel is `pages/assigned-plan-calendar`'s and a page may not import a page.
   */
  readonly renderSessionPanel: (workoutExecutionId: number, onClose: () => void) => ReactNode;
  /** The half the address asked for (`?scope=`), which the deck opens on. */
  readonly scope: DeckScope;
}

export function TasksScreen({ renderSessionPanel, scope }: TasksScreenProps) {
  const { t, i18n } = useTranslation();
  const screen = useTasksScreen(scope);
  const session = useInPlaceSession();
  const announcer = useAnnouncer();
  const reducedMotion = useScreenMotionPreference();
  const [termsOpen, setTermsOpen] = useState(false);
  /** Whether the trainer cleared this deck themselves. See the header — it is never written down. */
  const [clearedHere, setClearedHere] = useState(false);
  /**
   * Which way the card on the desk goes when it is answered, and whether the one on screen came
   * BACK rather than rose off the deck.
   *
   * The direction is the ACT's, so it is set inside the press rather than derived from the deck: by
   * the time the card unmounts, the answer it carried is already the previous state. `undone` is
   * cleared by the next decision, which is the only thing that can follow it.
   */
  const [deckMove, setDeckMove] = useState<{ side: DeckSide; undone: boolean }>({
    side: 'right',
    undone: false,
  });

  useDocumentTitle(t('web:tasks.documentTitle'));

  // The chat panel (chat canon, boards P1–P8): „Odpisz", „Napisz DM" and a row's chat glyph open the
  // client's conversation over the desk; another client swaps it without closing the panel.
  const roster = useQuery({ ...rosterQueryOptions(), select: (data) => buildClientRoster(data, null).rows });
  const chat = useClientChat((customerId) => {
    const row = roster.data?.find((entry) => entry.customerId === customerId);
    return row === undefined ? null : { email: row.email, planName: row.planName };
  });
  const openChat = chat.open;

  const { deck, setDeck } = screen;

  // A refused write returns its card and the deck shows it again. The sentence is the responder's.
  const onRefused = (keys: readonly string[]) => {
    setDeck((current) => (current === null ? current : unmarkHandled(current, keys)));
  };
  const responses = useHeldResponses({ onRefused, announce: announcer.announce });

  /**
   * The two closes that are NOT queue responses (R15) — a saved note and a survey marked reviewed. A
   * clip is resolved by the player on the client's card, which marks it seen. The server already has each of them, so nothing is held, nothing is posted to
   * `/attention-queue/responses`, and `Z` has nothing to take back. See `useImplicitResolution`.
   */
  const resolved = useImplicitResolution({
    announce: announcer.announce,
    // Keyed on the CARD rather than on the selection: the write settles a round trip later and the
    // trainer may have moved on, and striking whichever card they are looking at now would mark the
    // wrong one done.
    onResolved: (key, options) => {
      setDeck((current) => (current === null ? current : resolveImplicitly(current, key, options)));
    },
  });

  const cards = cardsInScope(deck);
  const selected = selectedCard(deck);
  const counts = deckCounts(deck);
  const progress = deckProgress(deck);
  const summary = deckSummary(deck);
  // The four terms resolve against a clock frozen on the visit's first render, the way
  // `useTasksScreen` freezes its own: `new Date()` in the body is a fresh value every render, which
  // makes this component impure and rebuilds four objects on every keystroke anywhere on the screen.
  // Nothing here is a live clock — a sitting that crossed 17:00 would keep offering "Dziś
  // wieczorem", and that is the same answer the menu was already showing when the trainer opened it.
  const [now] = useState(() => new Date());
  const presets = snoozePresets(now);
  const defaultPreset = presets.find((preset) => preset.isDefault) ?? presets[0];

  const taskSlots = useTaskSlots({
    announce: announcer.announce,
    reducedMotion,
    onResolved: (item) => {
      setDeck((current) => (current === null ? current : resolveImplicitly(current, item.key)));
    },
    onNoteSaved: (item) => {
      // Writing the note IS the resolution (R15). It is not a queue response: nothing is held,
      // nothing is posted, and `Z` has nothing to take back — the server already has the note.
      resolved.noteSaved(item.key, taskLabel(item, t));
    },
  });
  // The screen's own wait, which is a different one: the index reserves its rows while the queue
  // and the roster are open, and the strip reserves its squares while one schedule is.
  const list = useSkeletonGate(screen.status === 'pending');
  /**
   * The screen's wait branch. It is the GATE's verdict, not the read's, and the two differ by the
   * skeleton's 300ms minimum life: forking on `screen.status` flashed the rows in at full opacity
   * the instant the queue answered and then blinked them out and back when the hand-over arrived.
   * Everything the screen draws from the queue forks on this. `screen.status` stays the read.
   */
  const status = list.waiting ? 'pending' : screen.status;

  /**
   * A term's instant, worded — the time beside its word in the menu AND the words the live region
   * says back after the write. ONE wording, so the two cannot disagree about what was chosen.
   */
  const termWords = (preset: SnoozePreset): string =>
    snoozeTermWords(preset, now, {
      clock: (until) =>
        dateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(until),
      dayAndClock: (until) =>
        dateTimeFormat(i18n.language, {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }).format(until),
    });

  const decide = (
    kind: HandledKind,
    targets: readonly DeckCard[],
    drawn: SnoozePreset | undefined,
  ) => {
    if (targets.length === 0) return;
    // The TERM is what the trainer chose; its instant belongs to this press, not to the clock the
    // menu was drawn from. A tab opened yesterday would otherwise send an instant that has passed,
    // which R6 refuses, and every snooze would bounce.
    const preset = drawn === undefined ? undefined : snoozePresetAt(drawn.id, new Date());
    const commands: AttentionResponseCommand[] = targets.map((card) => ({
      key: card.item.key,
      anchorKey: card.item.anchorKey,
      reason: card.item.reason,
      kind: kind === 'done' ? 'Done' : 'Snoozed',
      subjectCustomerId: card.item.subjectCustomerId,
      task: taskLabel(card.item, t),
      snoozeUntilIso: kind === 'snoozed' ? (preset?.untilIso ?? null) : null,
      // R6 is about a CHOSEN time, so the live region says the term that was sent rather than the
      // default one. The Pulpit passes none and keeps its own sentence.
      snoozeTerm: kind === 'snoozed' && preset !== undefined ? termWords(preset) : null,
    }));
    setDeckMove({ side: kind === 'done' ? 'right' : 'left', undone: false });
    responses.decide({ kind, snoozeUntilIso: preset?.untilIso ?? null, commands });
    // The updater stays PURE — the clearing flag is decided here, where the deck before the
    // answer is in hand, rather than inside a setter React may call twice.
    const answeredKeys = commands.map((command) => command.key);
    if (deckIsCleared(markHandled(deck, answeredKeys, kind))) setClearedHere(true);
    setDeck((current) => (current === null ? current : markHandled(current, answeredKeys, kind)));
  };

  const answerSelected = (kind: HandledKind, preset?: SnoozePreset) => {
    if (selected === null || selected.handled !== null) return;
    decide(kind, [selected], preset ?? defaultPreset);
  };

  const undo = () => {
    const cancelled = responses.undo();
    if (cancelled === null) {
      announcer.announce(t('web:tasks.live.nothingToUndo'));
      return;
    }
    setClearedHere(false);
    setDeckMove((current) => ({ ...current, undone: true }));
    setDeck((current) => (current === null ? current : unmarkHandled(current, cancelled.keys)));
    const first = deck.cards.find((card) => card.item.key === cancelled.keys[0]);
    announcer.announce(
      t('web:tasks.live.undone', {
        task:
          cancelled.keys.length === 1 && first !== undefined
            ? taskLabel(first.item, t)
            : String(cancelled.keys.length),
      }),
    );
  };

  useTaskShortcuts({
    later: () => {
      answerSelected('snoozed');
    },
    done: () => {
      answerSelected('done');
    },
    previous: () => {
      setDeck((current) => (current === null ? current : stepSelection(current, -1)));
    },
    next: () => {
      setDeck((current) => (current === null ? current : stepSelection(current, 1)));
    },
    primary: () => {
      // The card's own primary is a link or a button it owns; the key only has to reach it.
      document.querySelector<HTMLElement>(`[${TASK_PRIMARY_ATTRIBUTE}]`)?.click();
    },
    undo,
    openTerms: () => {
      setTermsOpen(true);
    },
    // On the gated verdict, so a key cannot answer a card the trainer is still looking at as bars;
    // and never under the session panel, whose own Escape and reading keys are the keyboard's then.
    enabled: status === 'ready' && selected !== null && session.open === null,
  });

  const cleared = deckIsCleared(deck);
  const muted = screen.mutedReasons;
  const mutedLine = mutedSentence(muted, (key, values) => t(key, values));

  /**
   * The deck's swap, silent for the FIRST card the queue brings. The plate is keyed by card and
   * mounts only once the queue has answered, so without this guard the first card played the deck's
   * arrival gesture on top of the skeleton's hand-over: measured as 13px of travel and a scale on a
   * cold entry (22 September 2026). A card the trainer answered or undid still swaps; `exit` is the
   * step's own and is spread beside the guarded entrance.
   */
  const deckStep = deckSwap({
    leaving: deckMove.side,
    returningFrom: deckMove.undone ? deckMove.side : null,
    reducedMotion,
  });
  const deckEntrance = useMotionOnKeyChange(selected?.item.key ?? null, deckStep);

  return (
    <main className={`${shell.page} ${session.open === null ? '' : String(shell.pageHosting)}`}>
      {/* Behind the session panel while it is open: out of play for the keyboard and the reader. */}
      <motion.div
        className={`${shell.measure} ${styles.page}`}
        inert={session.open !== null}
        {...screenMotion('rise', { reducedMotion })}
      >
        <header className={styles.head}>
          <div>
            {/* Rendered in every state: `AppShell` moves focus here on arrival and announces it, so
              * a heading that waited for a read would announce nothing. */}
            <h1 className={styles.title}>{t('web:tasks.title')}</h1>
            <p className={styles.sub}>
              {status === 'ready' && !cleared
                ? summaryLine()
                : status === 'pending'
                  ? ''
                  : t('web:tasks.subEmpty')}
            </p>
          </div>
          <div className={styles.headRight}>
            <Link
              className={styles.cog}
              to="/zadania/rodzaje"
              aria-label={t(muted.length === 0 ? 'clients.taskPrefs.a11y.gear' : 'clients.taskPrefs.a11y.gearMuted')}
            >
              <CogIcon className={styles.cogIcon} />
              {muted.length === 0 ? null : <span className={styles.cogDot} aria-hidden="true" />}
            </Link>
          </div>
        </header>

        <LiveRegionAnnouncer message={announcer.message} />
        {taskSlots.dialog}

        {status === 'error' ? (
          <div className={styles.state}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={t('web:tasks.error.title')}
              hint={t('web:tasks.error.hint')}
            />
            <button className={`${ghost.ghost} ${styles.retry}`} type="button" onClick={screen.retry}>
              {t('web:clients.states.retry')}
            </button>
          </div>
        ) : status === 'empty' ? (
          /* Told, never congratulated: this queue was empty before the trainer arrived. */
          <div className={styles.state}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="positive"
              title={t('web:tasks.empty.title')}
              hint={t('web:tasks.empty.hint')}
            />
          </div>
        ) : (
          <div className={styles.grid}>
            <TaskQueueIndex
              reducedMotion={reducedMotion}
              cards={status === 'pending' ? [] : cards}
              selectedKey={deck.selectedKey}
              scope={deck.scope}
              counts={counts}
              progress={progress}
              onScope={(next: DeckScope) => {
                setDeck((current) => (current === null ? current : setScope(current, next)));
              }}
              onSelect={(key) => {
                setDeck((current) => (current === null ? current : selectCard(current, key)));
              }}
              age={age}
              bulkMenu={
                <BulkMenu
                  doneCount={bulkTargets(deck, 'done').length}
                  snoozeCount={bulkTargets(deck, 'snoozed').length}
                  onAct={bulk}
                />
              }
              onUndo={responses.held === null ? null : undo}
              pending={status === 'pending'}
              skeleton={list.skeleton}
              reveal={list.reveal}
              onOpenChat={openChat}
              talkingTo={chat.talkingTo}
            />

            <div className={`${styles.desk} ${cleared ? styles.deskCleared : ''}`}>
              {cleared ? (
                clearedHere ? (
                  <QueueDone tally={deckTally(deck)} onUndo={responses.held === null ? null : undo} />
                ) : (
                  <EmptyState
                    mark={<InboxMarkIcon />}
                    tone="positive"
                    title={t('web:tasks.empty.title')}
                    hint={t('web:tasks.empty.hint')}
                  />
                )
              ) : (
                <div className={`${styles.stack} ${list.reveal}`}>
                  {/* The deck. `AnimatePresence` keeps the answered card on screen while it leaves,
                    * and `.stack` is one grid CELL so the two never push each other — the outgoing
                    * card's box is the incoming card's box. Both are wrapped rather than animated
                    * on `TaskCard` itself, because the card is a composition this screen owns the
                    * placement of and the motion belongs to the DESK. */}
                  <AnimatePresence initial={false}>
                    {selected === null ? null : (
                      /* The plate carries the SWAP and nothing else. The hand-over sits on
                       * `.stack` above, which is unkeyed and stands from the first frame: `.reveal`
                       * is a keyframe with `fill-mode: both`, so an element that remounts on a
                       * gesture replays it, and this one remounts on every answered card. */
                      <motion.div
                        className={styles.plate}
                        key={selected.item.key}
                        {...deckEntrance}
                        exit={deckStep.exit}
                      >
                      <TaskCard
                        card={selected}
                        position={selectedPosition(deck)}
                        total={progress.total}
                        when={age(selected)}
                        now={screen.now}
                        slots={taskSlots.slots}
                        reducedMotion={reducedMotion}
                        primarySending={resolved.isSending(selected.item.key)}
                        primaryRefused={resolved.wasRefused(selected.item.key)}
                        onOpenSession={session.show}
                        clipField={taskSlots.clipField}
                        onOpenChat={openChat}
                        onPrimary={() => {
                          // "Oznacz jako przejrzane" — visible to the CLIENT (`intake-survey` R31)
                          // and not a queue response, which is private to the responder (R4). The
                          // note's composer is already open on this card.
                          if (selected.item.action.kind === 'markReviewed') {
                            resolved.markReviewed(
                              selected.item.key,
                              selected.item.action.surveyAssignmentId,
                              taskLabel(selected.item, t),
                            );
                          }
                        }}
                      />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* The second grid row, in the desk's column only: the two decisions stand ALONE under
              * the card with nothing beside them (owner, 18 September 2026). */}
            {cleared ? null : (
              <div className={styles.decide}>
                <DecisionBar
                  person={selected === null ? '' : taskLabel(selected.item, t)}
                  answered={selected !== null && selected.handled !== null}
                  presets={presets}
                  termWhen={termWords}
                  onLater={(preset) => {
                    setTermsOpen(false);
                    answerSelected('snoozed', preset);
                  }}
                  onDone={() => {
                    answerSelected('done');
                  }}
                  termsOpen={termsOpen}
                  onTermsOpenChange={setTermsOpen}
                />
              </div>
            )}
          </div>
        )}

        <div className={styles.foot}>
          {/* R18: a muted kind is NAMED on this screen, and the sentence now leads somewhere. */}
          {mutedLine === null ? (
            <span />
          ) : (
            <p className={styles.muted}>
              <MutedNote sentence={mutedLine} />
            </p>
          )}
          <p className={styles.legend}>
            <span className={styles.legendItem}>
              <kbd className={styles.key}>↑</kbd>
              <kbd className={styles.key}>↓</kbd> {t('web:tasks.keys.select')}
            </span>
            <span className={styles.legendItem}>
              <kbd className={styles.key}>⇧</kbd>
              <kbd className={styles.key}>←</kbd> {t('web:tasks.keys.term')}
            </span>
            <span className={styles.legendItem}>
              <kbd className={styles.key}>↵</kbd> {t('web:tasks.keys.primary')}
            </span>
            <span className={styles.legendItem}>
              <kbd className={styles.key}>Z</kbd> {t('web:tasks.keys.undo')}
            </span>
          </p>
        </div>
      </motion.div>
      {session.open === null ? null : renderSessionPanel(session.open, session.close)}
      {chat.dock === null ? null : <ChatDock {...chat.dock} />}
    </main>
  );

  /** "N zadań · X pilnych", the Pulpit's line; only "N zadań" while nothing waiting is urgent. */
  function summaryLine(): string {
    const tasks = t('web:tasks.summary', { count: summary.open });
    if (summary.urgent === 0) return tasks;
    return t('web:tasks.summaryWithUrgent', {
      tasks,
      urgent: t('web:tasks.summaryUrgent', { count: summary.urgent }),
    });
  }

  function bulk(act: BulkAct) {
    const keys = new Set(bulkTargets(deck, act));
    decide(act, deck.cards.filter((card) => keys.has(card.item.key)), defaultPreset);
    announcer.announce(
      act === 'done'
        ? t('web:tasks.live.bulkDone', { count: keys.size })
        : t('web:tasks.live.bulkSnoozed', {
            count: keys.size,
            // The bulk act writes the DEFAULT term, and says which one that is rather than leaving
            // a reader to assume — the same obligation R6 puts on a single snooze.
            when: defaultPreset === undefined ? '' : termWords(defaultPreset),
          }),
    );
  }

  /** How long the signal has been waiting, worded. `''` where the wire carried no stamp. */
  function age(card: DeckCard): string {
    const at = card.item.signalAtUtc;
    if (at === null) return '';
    return dateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(new Date(at));
  }



}
